import React, {useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {Autoplay} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {diApi} from '@di-shared/api/di';
import {
  AggregatedSestData,
  CreatedMaintenanceRecordRequest,
  GetMaintenanceRecordResponse,
  MaintenanceInfo,
} from '@di-shared/api/di/__generated';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {t, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import EngineOilAndFilterAdvancedTooltip from '@di-shared/features/diag/sest/EngineOilAndFilterAdvancedTooltip';
import {SestCode} from '@di-shared/features/diag/sest/const';
import useSestDetail, {ExpectedReplacementPeriodType} from '@di-shared/features/diag/sest/useSestDetail';
import useSestDetailEngineOilAndFilterAdvanced, {
  EngineOilLifeStatus,
} from '@di-shared/features/diag/sest/useSestDetailEngineOilAndFilterAdvanced';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

interface SestDetailEngineOilAndFilterProps {
  vin: string;
}

function SestDetailEngineOilAndFilter({vin}: SestDetailEngineOilAndFilterProps) {
  const {
    sestName,
    invalidateSestData,
    replacementStatus,
    maintenanceType,
    remainingDistance,
    remainingDays,
    stdDistance,
    stdDays,
    contentLevel,
    distanceReplacementStatus,
    dayReplacementStatus,
    gotoSestExchangeSelectPage,
    expectedReplacementPeriodType,
    replaceDate,
    replaceOdometer,
  } = useSestDetail(vin, CreatedMaintenanceRecordRequest.sestCode._01);
  const queryClient = useQueryClient();
  const {convertDistanceSymbol, convertDistanceValue, defaultDistanceSymbol} = useUnitConverter();
  const randomMessages = [t('T30287'), t('T30288'), t('T30289')];
  const [randomNumber] = useState(Math.floor(Math.random() * randomMessages.length));
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const parsedReplaceDate = parseDateWithFormat(replaceDate);
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord), vin, SestCode.ENGINE_OIL_AND_FILTER],
    queryFn: () => diApi.maintenanceRecordController.getMostRecentMaintenanceRecord({vin, sestCode: SestCode.ENGINE_OIL_AND_FILTER}),
  });
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{sestName}</h3>
        <button
          type="button"
          className="btn_setting"
          onClick={async () => {
            const response = await ccWebviewUtils.openBottomSheetWebViewUrl<false | null | {isChange?: boolean; historyId?: string}>(
              ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING.getFullPath(SestCode.ENGINE_OIL_AND_FILTER),
              {
                vin,
                replaceDate: parseDateWithFormat(data.data.replaceDate),
                replaceOdometer: data.data.replaceOdometer,
                isServiceCenterInput: data.data.source === GetMaintenanceRecordResponse.source.SERVICE_CENTER,
                historyId: data.data.id.toString(),
              }
            );

            if (!response) {
              return;
            }

            if (response.isChange) {
              await Promise.all([
                invalidateSestData(),
                queryClient.invalidateQueries({
                  queryKey: [
                    getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord),
                    vin,
                    SestCode.ENGINE_OIL_AND_FILTER,
                  ],
                }),
              ]);
            }
            if (response.historyId) {
              navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY_DETAIL_EDIT.getFullPath(vin, response.historyId));
            }
          }}
        >
          <span className="ic ic_setting_16px">
            <span className="blind">{t('T10005')}</span>
          </span>
        </button>
      </div>
      <div className="expendable_detail">
        <Swiper
          slidesPerView={'auto'}
          direction={'vertical'}
          modules={[Autoplay]}
          autoplay={{delay: 4000, disableOnInteraction: false}}
          effect={'flip'}
          observer
          observeParents
          autoHeight
          loop
          allowTouchMove={false}
          /**
           * [2025-10-01:Jeyoung Lee] FIXME: swiper 수정되면 사라질 인라인 스타일
           */
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
            position: 'relative',
            overflow: 'hidden',
            listStyle: 'none',
            zIndex: 1,
            display: 'block',
          }}
        >
          <SwiperSlide>
            <p className="date_desc">
              {parsedReplaceDate && CldrDatetimeFormatter.format(parsedReplaceDate, 'yMMMd')}
              <span className="change_text">
                {replaceOdometer
                  ? formatNumberWithCommas(convertDistanceValue(replaceOdometer)) + convertDistanceSymbol(remainingDistance)
                  : ERROR.DASH + defaultDistanceSymbol}
                &nbsp;{t('T30296')}
              </span>
            </p>
          </SwiperSlide>
          {replacementStatus === MaintenanceInfo.status.REQUIRED && (
            <SwiperSlide>
              <p className="expendable_desc type_warning">
                <span className="ic ic_filled_caution_16px"></span> {t('T30025')}
              </p>
            </SwiperSlide>
          )}
          {replacementStatus === MaintenanceInfo.status.RECOMMENDED && (
            <SwiperSlide>
              <p className="expendable_desc type_warning">
                <span className="ic ic_filled_caution_16px"></span> {t('T30026')}
              </p>
            </SwiperSlide>
          )}
        </Swiper>
        <p className="expendable_desc">
          {expectedReplacementPeriodType === ExpectedReplacementPeriodType.COMBINED
            ? t('T30326')
            : expectedReplacementPeriodType === ExpectedReplacementPeriodType.DISTANCE
              ? t('T30387')
              : ''}
        </p>
        <ul className="expendable_list">
          {(maintenanceType === AggregatedSestData.maintenanceType.COMBINED ||
            maintenanceType === AggregatedSestData.maintenanceType.DISTANCE) && (
            <li
              className={clsx('expendable_item', {
                type_warning:
                  distanceReplacementStatus === MaintenanceInfo.status.REQUIRED ||
                  distanceReplacementStatus === MaintenanceInfo.status.RECOMMENDED,
              })}
            >
              <div className="expendable_icon_box">
                <span className="ic ic_engine_oil_24px"></span>
              </div>
              <div className="expendable_info">
                <span className="expendable_info_title">
                  {t('T30028_$distance', {
                    distance: remainingDistance
                      ? formatNumberWithCommas(convertDistanceValue(remainingDistance)) + convertDistanceSymbol(remainingDistance)
                      : ERROR.DASH + defaultDistanceSymbol,
                  })}
                </span>
                <span className="expendable_info_base">
                  {t('T30029_$distance', {
                    distance: stdDistance
                      ? formatNumberWithCommas(convertDistanceValue(stdDistance)) + convertDistanceSymbol(stdDistance)
                      : ERROR.DASH + defaultDistanceSymbol,
                  })}
                </span>
              </div>
            </li>
          )}
          {(maintenanceType === AggregatedSestData.maintenanceType.COMBINED ||
            maintenanceType === AggregatedSestData.maintenanceType.DAY) && (
            <li
              className={clsx('expendable_item', {
                type_warning:
                  dayReplacementStatus === MaintenanceInfo.status.REQUIRED || dayReplacementStatus === MaintenanceInfo.status.RECOMMENDED,
              })}
            >
              <div className="expendable_icon_box">
                <span className="ic ic_history_24px"></span>
              </div>
              <div className="expendable_info">
                <span className="expendable_info_title">{t('T30030_$day', {day: remainingDays})}</span>
                <span className="expendable_info_base">{t('T30031_$day', {day: stdDays})}</span>
              </div>
            </li>
          )}
        </ul>
      </div>

      {contentLevel === AggregatedSestData.contentLevel.ADVANCED && (
        <ErrorBoundary fallback={<></>}>
          <SestDetailEngineOilAndFilterAdvanced vin={vin} />
          <p className="notice_refer">{randomMessages[randomNumber]}</p>
        </ErrorBoundary>
      )}
      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={() => {
          emitEventLog({
            event: 'consumables_exchange_update',
            form_step: 'cta click',
            click_text: t('T30027'),
            click_location: 'body',
          });
          gotoSestExchangeSelectPage();
        }}
      >
        {t('T30027')}
      </button>
    </div>
  );
}

interface SestDetailEngineOilAndFilterAdvancedProps {
  vin: string;
}

function SestDetailEngineOilAndFilterAdvanced({vin}: SestDetailEngineOilAndFilterAdvancedProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {engineOilLifeStatus, avgLife, finalLife, updateDate} = useSestDetailEngineOilAndFilterAdvanced(vin);
  const engineOilLifeStatusText = (() => {
    switch (engineOilLifeStatus) {
      case EngineOilLifeStatus.GOOD:
        return t('T30032');
      case EngineOilLifeStatus.NORMAL:
        return t('T30033');
      case EngineOilLifeStatus.WARNING:
        return t('T30034');
      case EngineOilLifeStatus.BAD:
        return t('T30035');
      default:
        throw new Error(`unknown engineOilLifeStatus: ${engineOilLifeStatus}`);
    }
  })();
  const ADVANCED_GRAPH_MAX_HEIGHT = 60;
  const avgLifeGraphHeight = avgLife > finalLife ? ADVANCED_GRAPH_MAX_HEIGHT : ADVANCED_GRAPH_MAX_HEIGHT * (avgLife / finalLife);
  const finalLifeGraphHeight = finalLife > avgLife ? ADVANCED_GRAPH_MAX_HEIGHT : ADVANCED_GRAPH_MAX_HEIGHT * (finalLife / avgLife);

  return (
    <>
      <div className="section_container_box">
        <div className="expendable_advance">
          <h4
            className="expendable_advance_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {engineOilLifeStatusText}
          </h4>
          <div
            className="expendable_advance_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30036')}
            <EngineOilAndFilterAdvancedTooltip />
          </div>
          <div className="oil_life_graph_area">
            <div className="bar_graph type_average">
              <div className="bar_graph_item average_item">
                <span className="speech_bubble">{Math.round(avgLife)}%</span>
                <span
                  className="graph_bar"
                  style={{height: avgLifeGraphHeight}}
                ></span>
                <span className="graph_title">{t('T30037')}</span>
              </div>
              <div className="bar_graph_item my_item is-active">
                <span className="speech_bubble">{Math.round(finalLife)}%</span>
                <span
                  className="graph_bar"
                  style={{height: finalLifeGraphHeight}}
                ></span>
                <span className="graph_title">{t('T30325')}</span>
              </div>
            </div>
          </div>
          <span className="expendable_advance_update">
            {t('T30039')}: {updateDate && CldrDatetimeFormatter.format(updateDate, 'yMMMd')}
          </span>
        </div>
      </div>
    </>
  );
}

export default SestDetailEngineOilAndFilter;
